import i18n from "@/i18n";
import { BASE_URL } from "@/config/env";

const EIRTECH_URL = "https://www.eirtech.ai/";
const LEGAL_OPERATOR_NAME = "EirTech AB";
const ORGANIZATION_NUMBER = "559522-3800";

/**
 * Stable node ids for the two site-wide entities. Every page emits exactly one
 * Organization and one WebSite; anything that needs to point at them (a page's
 * `publisher`, `provider`, `author`) references the id instead of inlining a
 * second copy of the same entity, which Google reads as a duplicate item.
 */
export const ORGANIZATION_ID = `${BASE_URL}/#organization`;
export const WEBSITE_ID = `${BASE_URL}/#website`;

/** A reference to the single Organization node, for use as publisher/provider. */
export const caireOrganizationRef = { "@id": ORGANIZATION_ID };

/**
 * The registered postal address, in one place so page copy and structured data
 * cannot disagree. They did: this file said "131 58" while the contact page,
 * the about/contact locales, the legal pages, the email footers and the shared
 * SEO package said "13151". Everything is aligned on the value the structured
 * data has been publishing to Google, but both are valid Nacka postcodes and
 * confirming which is registered is a question for the business — that is why
 * it lives here, so correcting it is one line rather than fifteen files.
 */
export const CAIRE_POSTAL_ADDRESS = {
  streetAddress: "Tändkulevägen 33",
  postalCode: "131 58",
  addressLocality: "Nacka",
  addressCountry: "SE",
} as const;

/** The PostalAddress node, shared by every schema in this file. */
const cairePostalAddressNode = {
  "@type": "PostalAddress",
  addressCountry: CAIRE_POSTAL_ADDRESS.addressCountry,
  streetAddress: CAIRE_POSTAL_ADDRESS.streetAddress,
  postalCode: CAIRE_POSTAL_ADDRESS.postalCode,
  addressLocality: CAIRE_POSTAL_ADDRESS.addressLocality,
};

// Was a full second copy of the Caire Organization node — same name, legalName,
// url and org.nr as the top-level one — which Google reads as a duplicate item
// rather than a reference. Point at the single node instead.
const cairePublisher = caireOrganizationRef;

// Function to get organization data with translations
export const getOrganizationData = (): Record<string, unknown> => {
  const t = i18n.t;
  const currentLang = i18n.language || "sv";

  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: t("seo.site", "Caire"),
    legalName: LEGAL_OPERATOR_NAME,
    url: BASE_URL,
    logo: {
      "@type": "ImageObject",
      // SVG stays. An earlier revision of this branch swapped it for a PNG on
      // the belief that Google's Organization logo rejects SVG. That is wrong:
      // the logo docs require a format "supported by Google Images", and that
      // list is "BMP, GIF, JPEG, PNG, WebP, SVG, and AVIF". Only the quoted
      // dimensions were actually invalid — schema.org types width and height
      // numerically — so only they change.
      url: `${BASE_URL}/favicons/favicon-caire-v2.svg`,
      width: 512,
      height: 512,
    },
    description: t(
      "seo.default.description",
      "AI-driven schemaläggning för hemtjänst",
    ),
    slogan: t(
      "seo.default.ogDescription",
      "Intelligent planering för hemtjänst",
    ),
    founder: {
      "@type": "Person",
      name: "Björn Evers",
      jobTitle: t("seo.organization.founder.jobTitle", "Grundare & VD"),
      sameAs: "https://se.linkedin.com/in/bjornevers",
    },
    address: cairePostalAddressNode,
    // Both contact points live on the single Organization node. The contact
    // page used to emit its own Organization to carry the support one, which
    // duplicated the entity — and shipped a different postcode ("13151")
    // than the canonical address below.
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        email: "info@caire.se",
        availableLanguage: [
          t("seo.organization.languages.swedish", "Svenska"),
          t("seo.organization.languages.english", "Engelska"),
        ],
      },
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "info@caire.se",
        availableLanguage: [
          t("seo.organization.languages.swedish", "Svenska"),
          t("seo.organization.languages.english", "Engelska"),
        ],
      },
    ],
    // Caire's own identity URLs only. `sameAs` means "other URLs that refer to
    // THIS entity", so listing eirtech.ai claimed Caire and EirTech AB are the
    // same organisation. They are not — EirTech is the parent, and it is
    // already stated as `parentOrganization` below, which is the property that
    // actually expresses that relationship.
    sameAs: ["https://www.linkedin.com/company/caire-homecare"],
    parentOrganization: {
      "@type": "Organization",
      name: LEGAL_OPERATOR_NAME,
      url: EIRTECH_URL,
      identifier: {
        "@type": "PropertyValue",
        propertyID: "SE org.nr",
        value: ORGANIZATION_NUMBER,
      },
    },
    knowsAbout: [
      t("seo.organization.knowsAbout.scheduling", "Schemaläggning"),
      t("seo.organization.knowsAbout.administration", "Administration"),
      t("seo.organization.knowsAbout.optimization", "Optimering"),
      t("seo.organization.knowsAbout.staffPlanning", "Personalplanering"),
      t("seo.organization.knowsAbout.solutions", "Digitala lösningar"),
      t("seo.organization.knowsAbout.municipal", "Kommunal hemtjänst"),
      t("seo.organization.knowsAbout.private", "Privat hemtjänst"),
    ],
    keywords: t("seo.default.keywords", "AI, hemtjänst, schemaläggning")
      .split(",")
      .map((k) => k.trim()),
    areaServed: [
      {
        "@type": "Country",
        name: t("seo.organization.country.name", "Sverige"),
        alternateName: t("seo.organization.country.alternateName", "Sweden"),
      },
    ],
    // `price: "0"` used to sit here next to a "contact us for a walkthrough"
    // description — a fabricated price for a paid product. The real public
    // entry price is on /prissattning and is published on the
    // SoftwareApplication node in Index.tsx; the Organization does not need a
    // second, conflicting Offer.
  };
};

// Update website schema to use a function that provides translations
export const getWebsiteSchema = (): Record<string, unknown> => {
  const t = i18n.t;
  const currentLang = i18n.language || "sv";
  const locale = currentLang === "en" ? "en-US" : "sv-SE";

  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: BASE_URL,
    name: t("seo.site", "Caire"),
    description: t(
      "seo.default.description",
      "AI-driven schemaläggning för hemtjänst",
    ),
    inLanguage: locale,
    copyrightYear: new Date().getFullYear().toString(),
    copyrightHolder: {
      "@type": "Organization",
      name: LEGAL_OPERATOR_NAME,
      url: EIRTECH_URL,
    },
    publisher: cairePublisher,
    alternateName: t("seo.website.alternateName", "Caire Schemaläggning"),
  };
};

// Define lazy-loaded website schema
const getLazyWebsiteSchema = () => {
  return {
    "@type": "WebSite",
    name: "Caire",
    url: BASE_URL,
    inLanguage: "sv-SE",
    copyrightYear: new Date().getFullYear().toString(),
    copyrightHolder: {
      "@type": "Organization",
      name: LEGAL_OPERATOR_NAME,
      url: EIRTECH_URL,
    },
    publisher: cairePublisher,
  };
};

// Export the static website schema through a getter function
export const websiteSchema = getLazyWebsiteSchema();
